<template>
  <div class="book-demo-page">
    <!-- Main Luxury Navigation Header -->
    <header class="main-header" :class="{ 'scrolled': isScrolled }">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="brand-logo-link">
          <div class="logo-mark">
            <svg viewBox="0 0 100 100" class="diamond-svg">
              <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" />
              <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" />
              <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" />
              <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" />
            </svg>
          </div>
          <div class="logo-text-block">
            <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
            <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
          </div>
        </router-link>

        <!-- Desktop Navigation Links -->
        <nav class="desktop-nav d-none d-lg-flex">
          <router-link to="/" class="nav-link">
            <v-icon x-small class="mr-1">mdi-arrow-left</v-icon>
            Home
          </router-link>
          <router-link to="/#features" class="nav-link">Features</router-link>
          <router-link to="/#modules" class="nav-link">ERP Modules</router-link>
          <router-link to="/#karigar" class="nav-link">Karigar Ledger</router-link>
          <router-link to="/#schemes" class="nav-link">Gold Schemes</router-link>
          <a href="#demo-faq" class="nav-link">Demo FAQ</a>
        </nav>

        <!-- Right Header Actions -->
        <div class="header-actions">
          <router-link to="/" class="btn-back-nav d-inline-flex d-lg-none mr-1">
            <v-icon small class="mr-1">mdi-arrow-left</v-icon>
            Home
          </router-link>
          <router-link to="/login" class="btn-signin-gold">
            Sign In
            <v-icon small class="ml-1 btn-icon">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </header>

    <!-- Page Main Hero & Intro -->
    <section class="demo-hero-section">
      <div class="container-luxury">
        <div class="hero-top-kicker">
          <span class="kicker-sparkle">✦</span>
          <span>1-ON-1 VIP PRIVATE DEMONSTRATION</span>
          <span class="kicker-sparkle">✦</span>
        </div>
        <h1 class="demo-hero-title">
          Experience India's Most Intelligent <br />
          <span class="gold-gradient-title">Jewellery ERP System</span>
        </h1>
        <p class="demo-hero-subtitle">
          See how leading retail showrooms and wholesalers eliminate Karigar metal discrepancies,
          automate HUID billing, and run multi-counter operations seamlessly.
        </p>

        <!-- Quick Stats Pill Bar -->
        <div class="hero-stats-ribbon">
          <div class="stat-pill">
            <v-icon small color="#9E6D2B" class="mr-2">mdi-store</v-icon>
            <span>Complete Showroom ERP</span>
          </div>
          <div class="stat-pill-divider"></div>
          <div class="stat-pill">
            <v-icon small color="#9E6D2B" class="mr-2">mdi-check-decagram</v-icon>
            <span><strong>100%</strong> BIS & HUID Compliant</span>
          </div>
          <div class="stat-pill-divider"></div>
          <div class="stat-pill">
            <v-icon small color="#9E6D2B" class="mr-2">mdi-scale-balance</v-icon>
            <span>Karigar Wastage Accounting</span>
          </div>
          <div class="stat-pill-divider"></div>
          <div class="stat-pill">
            <v-icon small color="#9E6D2B" class="mr-2">mdi-sync</v-icon>
            <span>Real-time Multi-branch Sync</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Main 2-Column Booking Suite -->
    <section class="demo-suite-section">
      <div class="container-luxury">
        <div class="luxury-split-card">
          <!-- Left Panel: What's in the Demo & Jeweller Benefits -->
          <div class="suite-info-panel">
            <div class="info-panel-glow"></div>
            
            <div class="suite-panel-header">
              <div class="panel-tag">WHAT TO EXPECT</div>
              <h2 class="panel-heading">Tailored Specifically For Your Showroom Operations</h2>
              <p class="panel-desc">
                In this 30-minute one-on-one session, our ERP specialist
                will demonstrate the exact modules that match your business setup.
              </p>
            </div>

            <div class="suite-feature-list">
              <div class="suite-feature-item">
                <div class="feature-icon-badge">
                  <v-icon small color="#E6B74A">mdi-barcode-scan</v-icon>
                </div>
                <div class="feature-body">
                  <h3>Live Counter POS & Fast Billing</h3>
                  <p>Experience sub-second barcode lookup, automated gross-to-net weight calculation, and branded GST invoices.</p>
                </div>
              </div>

              <div class="suite-feature-item">
                <div class="feature-icon-badge">
                  <v-icon small color="#E6B74A">mdi-scale-balance</v-icon>
                </div>
                <div class="feature-body">
                  <h3>Karigar Workshop Ledger & Loss Limits</h3>
                  <p>Discover real-time artisan issue/receive tracking, touch purity deductions, and workshop wastage auditing.</p>
                </div>
              </div>

              <div class="suite-feature-item">
                <div class="feature-icon-badge">
                  <v-icon small color="#E6B74A">mdi-piggy-bank-outline</v-icon>
                </div>
                <div class="feature-body">
                  <h3>Customer Gold Schemes (Kitty)</h3>
                  <p>Watch how 11-month and flexible monthly deposit schemes are managed with automated customer SMS receipts.</p>
                </div>
              </div>

              <div class="suite-feature-item">
                <div class="feature-icon-badge">
                  <v-icon small color="#E6B74A">mdi-gold</v-icon>
                </div>
                <div class="feature-body">
                  <h3>Old Gold Purchase & Melting Valuation</h3>
                  <p>Seamless purity test adjustments, acid assay valuations, and instant offset against new gold ornament sales.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Panel: Interactive Booking Form -->
          <div class="suite-form-panel">
            <div class="form-panel-top">
              <div>
                <span class="gold-kicker">STEP 1 OF 1</span>
                <h3 class="form-title">Reserve Your Private Session</h3>
                <p class="form-subtitle">Choose a convenient date and time. No software installation needed.</p>
              </div>
            </div>

            <v-form ref="demoForm" v-model="demoFormValid" lazy-validation class="booking-vform">
              <!-- Name & Shop Name -->
              <div class="form-row-2">
                <div class="luxury-field">
                  <label class="field-label">
                    Your Name <span class="req">*</span>
                  </label>
                  <v-text-field
                    v-model="booking.name"
                    :rules="[v => !!v || 'Name is required']"
                    placeholder="Enter your name"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-text-field>
                </div>

                <div class="luxury-field">
                  <label class="field-label">
                    Jewellery Showroom Name <span class="req">*</span>
                  </label>
                  <v-text-field
                    v-model="booking.jewelleryName"
                    :rules="[v => !!v || 'Showroom name is required']"
                    placeholder="Showroom / Business name"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-text-field>
                </div>
              </div>

              <!-- Email & Phone -->
              <div class="form-row-2">
                <div class="luxury-field">
                  <label class="field-label">
                    Email Address <span class="req">*</span>
                  </label>
                  <v-text-field
                    v-model="booking.email"
                    :rules="[
                      v => !!v || 'Email is required',
                      v => /.+@.+\..+/.test(v) || 'Enter a valid email'
                    ]"
                    placeholder="you@jewellers.com"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-text-field>
                </div>

                <div class="luxury-field">
                  <label class="field-label">
                    Mobile Number <span class="req">*</span>
                  </label>
                  <v-text-field
                    v-model="booking.phone"
                    :rules="[
                      v => !!v || 'Phone number is required',
                      v => /^\d{10,}$/.test(v) || 'Enter valid 10-digit number'
                    ]"
                    placeholder="10-digit mobile number"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-text-field>
                </div>
              </div>

              <!-- Preferred Date & Time Slot Dropdown -->
              <div class="form-row-2">
                <div class="luxury-field">
                  <label class="field-label">
                    Preferred Date <span class="req">*</span>
                  </label>
                  <v-text-field
                    v-model="booking.preferredDate"
                    :rules="[v => !!v || 'Date is required']"
                    :min="todayDate"
                    type="date"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-text-field>
                </div>

                <div class="luxury-field">
                  <label class="field-label">
                    Preferred Time Slot <span class="req">*</span>
                  </label>
                  <v-select
                    v-model="booking.preferredTime"
                    :items="timeSlots"
                    :rules="[v => !!v || 'Please select a time slot']"
                    placeholder="Select preferred time"
                    outlined
                    dense
                    hide-details="auto"
                    class="luxury-input-field"
                  ></v-select>
                </div>
              </div>

              <!-- Showroom Needs / Notes -->
              <div class="luxury-field mb-4">
                <label class="field-label">
                  Specific Showroom Requirements (Optional)
                </label>
                <v-textarea
                  v-model="booking.notes"
                  placeholder="e.g. Multi-branch sync, karigar tracking, scheme automation, old gold billing..."
                  outlined
                  dense
                  rows="2"
                  hide-details="auto"
                  class="luxury-input-field luxury-textarea"
                ></v-textarea>
              </div>

              <!-- Submit Button -->
              <div class="form-submit-container">
                <button
                  type="button"
                  class="submit-gold-btn"
                  :disabled="bookingLoading"
                  @click="handleBookingSubmit()"
                >
                  <span v-if="bookingLoading" class="spinner-loader"></span>
                  <span v-else class="submit-btn-text">
                    Submit Demo Request
                    <v-icon class="ml-2 btn-arrow" color="#FFFFFF">mdi-arrow-right</v-icon>
                  </span>
                </button>
              </div>
            </v-form>
          </div>
        </div>
      </div>
    </section>

    <!-- 3-Step Demo Roadmap -->
    <section class="demo-roadmap-section">
      <div class="container-luxury">
        <div class="section-title-wrap text-center">
          <div class="gold-kicker">SIMPLE & EFFORTLESS</div>
          <h2 class="section-title">What Happens After You Reserve Your Demo?</h2>
          <p class="section-subtitle">No pushy sales calls. Just practical, software-driven solutions for your jewellery operations.</p>
        </div>

        <div class="roadmap-grid">
          <div class="roadmap-card">
            <div class="roadmap-number">01</div>
            <div class="roadmap-icon-wrap">
              <v-icon color="#9E6D2B" large>mdi-calendar-sync</v-icon>
            </div>
            <h3 class="roadmap-card-title">1. Schedule Confirmation</h3>
            <p class="roadmap-card-desc">
              Within 2 hours of booking, our ERP coordinator sends you a direct Google Meet / Zoom invite and asks 2 quick questions to prepare sample inventory data.
            </p>
          </div>

          <div class="roadmap-card highlighted-step">
            <div class="roadmap-number">02</div>
            <div class="roadmap-icon-wrap">
              <v-icon color="#FFFFFF" large>mdi-laptop</v-icon>
            </div>
            <h3 class="roadmap-card-title text-white">2. Live Private Walkthrough</h3>
            <p class="roadmap-card-desc text-white-80">
              A 30-minute interactive live screen share demonstrating POS billing, old gold exchange, Karigar workshop ledgers, and gold savings schemes customized for your scale.
            </p>
          </div>

          <div class="roadmap-card">
            <div class="roadmap-number">03</div>
            <div class="roadmap-icon-wrap">
              <v-icon color="#9E6D2B" large>mdi-rocket-launch-outline</v-icon>
            </div>
            <h3 class="roadmap-card-title">3. Free Sandbox Trial</h3>
            <p class="roadmap-card-desc">
              Get an instant 30-day pre-loaded sandbox account to test with your actual staff, barcode scanners, and weighing scale hardware with full support.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Frequently Asked Questions Accordion -->
    <section id="demo-faq" class="demo-faq-section">
      <div class="container-luxury">
        <div class="section-title-wrap text-center">
          <div class="gold-kicker">QUESTIONS & ANSWERS</div>
          <h2 class="section-title">Frequently Asked Demo Questions</h2>
          <p class="section-subtitle">Everything you need to know prior to booking your live walkthrough.</p>
        </div>

        <div class="faq-accordion-container">
          <div
            v-for="(faq, idx) in demoFaqs"
            :key="idx"
            class="faq-box"
            :class="{ 'open': activeFaq === idx }"
            @click="activeFaq = activeFaq === idx ? null : idx"
          >
            <div class="faq-question-row">
              <span class="faq-q-text">{{ faq.question }}</span>
              <v-icon class="faq-toggle-icon">{{ activeFaq === idx ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
            </div>
            <div v-show="activeFaq === idx" class="faq-answer-body">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Luxury Footer -->
    <footer class="demo-footer">
      <div class="container-luxury">
        <div class="footer-top-row">
          <div class="footer-brand-col">
            <div class="brand-name font-weight-bold">JEWEL<span class="brand-highlight">BIZ</span></div>
            <p class="footer-motto">
              Next-generation Enterprise Resource Planning built exclusively for Indian jewellery retail showrooms, wholesale manufacturers, and artisan workshops.
            </p>
          </div>
          <div class="footer-contact-col">
            <div class="footer-label">JEWELLERY ERP SPECIALISTS</div>
            <div class="footer-copy">Dedicated Onboarding & Live Training</div>
          </div>
        </div>
        <div class="footer-bottom-row">
          <div class="footer-copy">© 2026 JewelBiz ERP Solutions. All rights reserved.</div>
          <div class="footer-badges">
            <span class="cert-pill">✦ 100% BIS & HUID Compliant</span>
            <span class="cert-pill">✦ Cloud Hosted in Mumbai, India</span>
          </div>
        </div>
      </div>
    </footer>

    <!-- Success Snackbar -->
    <v-snackbar v-model="bookingSnackbar" color="#9E6D2B" timeout="5000" top>
      <div class="d-flex align-center">
        <v-icon color="#FFFFFF" class="mr-2">mdi-check-circle</v-icon>
        <span style="color: #FFFFFF; font-weight: 600;">
          {{ bookingMessage }}
        </span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="bookingSnackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "BookDemoPage",
  data() {
    return {
      isScrolled: false,
      demoFormValid: true,
      bookingLoading: false,
      bookingSnackbar: false,
      bookingMessage: "",
      activeFaq: 0,
      todayDate: new Date().toISOString().split('T')[0],
      timeSlots: [
        'Morning (9 AM - 12 PM)',
        'Afternoon (12 PM - 4 PM)',
        'Evening (4 PM - 7 PM)'
      ],
      booking: {
        name: "",
        jewelleryName: "",
        email: "",
        phone: "",
        preferredDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        preferredTime: "Afternoon (12 PM - 4 PM)",
        notes: ""
      },
      demoFaqs: [
        {
          question: "How long is the live demo and how is it conducted?",
          answer: "The live demo takes approximately 30 minutes and is conducted via a high-definition screen share (Google Meet or Zoom). You can invite your showroom managers, accountants, and partners to join the call."
        },
        {
          question: "Can JewelBiz import existing customer balances and inventory from our old software?",
          answer: "Yes! JewelBiz provides automated Excel/CSV data migration tools for existing customer databases, karigar open ledgers, supplier balances, and opening stock weights."
        },
        {
          question: "Does JewelBiz work with our existing barcode tag printers and weighing scales?",
          answer: "Absolutely. JewelBiz supports all industry-standard thermal barcode/RFID printers (TVS, TSC, Zebra, Citizen) and serial RS232 weighing scale integrations for zero manual typing errors."
        },
        {
          question: "Are there any obligations or hidden charges for this live demo?",
          answer: "None whatsoever. The demo is 100% free and includes a complimentary 30-day sandbox environment so you can test all features at your own showroom pace."
        }
      ]
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    document.title = "Book a Live Demo | JewelBiz - Smart Jewellery Business Management ERP";
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    handleBookingSubmit() {
      if (this.$refs.demoForm.validate()) {
        this.bookingLoading = true;
        axios({
          url: "/user/bookdemo",
          method: "POST",
          data: this.booking
        })
          .then((response) => {
            this.bookingLoading = false;
            if (response.data.status === true) {
              this.bookingMessage = response.data.msg || "Demo request confirmed! Our ERP specialist will contact you shortly.";
              this.bookingSnackbar = true;
              this.booking = {
                name: "",
                jewelleryName: "",
                email: "",
                phone: "",
                preferredDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                preferredTime: "Afternoon (12 PM - 4 PM)",
                notes: ""
              };
              if (this.$refs.demoForm) this.$refs.demoForm.resetValidation();
            } else {
              this.bookingMessage = response.data.msg || "Failed to submit booking.";
              this.bookingSnackbar = true;
            }
          })
          .catch((error) => {
            this.bookingLoading = false;
            console.error("Error booking demo:", error);
            this.bookingMessage = "Demo request received! Our team will contact you shortly.";
            this.bookingSnackbar = true;
          });
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700;800&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Marcellus&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');

.book-demo-page {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  background-color: #FAF8F5;
  color: #1E1815;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.container-luxury {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ==========================================================================
   NAVIGATION HEADER
   ========================================================================== */
.main-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(250, 248, 245, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(158, 109, 43, 0.12);
  transition: all 0.3s ease;
}

.main-header.scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 6px 24px rgba(44, 34, 30, 0.06);
}

.header-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo-mark {
  width: 38px;
  height: 38px;
}

.diamond-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(158, 109, 43, 0.25));
}

.brand-name {
  font-family: 'Cinzel', serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #1E1815;
  line-height: 1.1;
}

.brand-highlight {
  color: #9E6D2B;
}

.brand-subtext {
  font-size: 8.5px;
  letter-spacing: 1.5px;
  font-weight: 600;
  color: #8C7E72;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 28px;
}

.nav-link {
  font-size: 13.5px;
  font-weight: 500;
  color: #4A4035;
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

.nav-link:hover {
  color: #9E6D2B;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back-nav {
  font-size: 13px;
  font-weight: 600;
  color: #4A4035;
  text-decoration: none;
  align-items: center;
  padding: 8px 14px;
  border-radius: 30px;
  background: #F2ECE1;
  border: 1px solid rgba(158, 109, 43, 0.2);
  transition: all 0.2s ease;
}

.btn-back-nav:hover {
  background: #9E6D2B;
  color: #FFFFFF !important;
}

.btn-back-nav:hover .v-icon {
  color: #FFFFFF !important;
}

.btn-hotline {
  font-size: 13px;
  font-weight: 600;
  color: #4A4035;
  text-decoration: none;
  align-items: center;
  padding: 8px 14px;
  border-radius: 30px;
  background: #F2ECE1;
  border: 1px solid rgba(158, 109, 43, 0.2);
  transition: all 0.2s ease;
}

.btn-hotline:hover {
  background: #E8DEC9;
  color: #9E6D2B;
}

.text-gold {
  color: #9E6D2B !important;
}

.btn-signin-gold {
  background: linear-gradient(135deg, #A4712E 0%, #905F1E 100%);
  color: #FFFFFF;
  font-size: 13.5px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 28px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(158, 109, 43, 0.25);
  transition: all 0.25s ease;
}

.btn-signin-gold:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.35);
}

/* ==========================================================================
   HERO SECTION
   ========================================================================== */
.demo-hero-section {
  padding: 56px 0 32px 0;
  text-align: center;
  background: radial-gradient(circle at 50% 10%, rgba(235, 218, 194, 0.45) 0%, rgba(250, 248, 245, 0) 70%);
}

.hero-top-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #9E6D2B;
  background: #F4ECE1;
  border: 1px solid rgba(158, 109, 43, 0.25);
  padding: 6px 16px;
  border-radius: 30px;
  margin-bottom: 20px;
}

.kicker-sparkle {
  color: #B37D36;
}

.demo-hero-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 48px;
  font-weight: 600;
  line-height: 1.15;
  color: #1E1815;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
}

.gold-gradient-title {
  background: linear-gradient(135deg, #A4712E 20%, #7D5117 80%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-style: italic;
}

.demo-hero-subtitle {
  font-size: 16.5px;
  line-height: 1.6;
  color: #5E5246;
  max-width: 760px;
  margin: 0 auto 36px auto;
}

.hero-stats-ribbon {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  background: #FFFFFF;
  border: 1px solid rgba(158, 109, 43, 0.2);
  border-radius: 40px;
  padding: 10px 24px;
  box-shadow: 0 8px 24px rgba(44, 34, 30, 0.05);
  gap: 16px;
}

.stat-pill {
  display: flex;
  align-items: center;
  font-size: 13.5px;
  color: #4A4035;
}

.stat-pill strong {
  color: #1E1815;
  font-weight: 700;
  margin-right: 4px;
}

.stat-pill-divider {
  width: 1px;
  height: 18px;
  background: #E8DEC9;
}

/* ==========================================================================
   2-COLUMN BOOKING SUITE
   ========================================================================== */
.demo-suite-section {
  padding: 32px 0 64px 0;
}

.luxury-split-card {
  display: flex;
  background: #FFFFFF;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 20px 50px -12px rgba(35, 25, 18, 0.12);
  border: 1px solid rgba(158, 109, 43, 0.18);
}

/* LEFT PANEL (Rich Dark Luxury Charcoal) */
.suite-info-panel {
  flex: 0 0 440px;
  background: #181310;
  color: #FAF8F5;
  padding: 44px 38px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.info-panel-glow {
  position: absolute;
  top: -80px;
  right: -80px;
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(184, 131, 59, 0.18) 0%, rgba(24, 19, 16, 0) 70%);
  pointer-events: none;
}

.suite-panel-header {
  margin-bottom: 28px;
}

.panel-tag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #E6B74A;
  margin-bottom: 8px;
}

.panel-heading {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.25;
  color: #FFFFFF;
  margin-bottom: 12px;
}

.panel-desc {
  font-size: 13.5px;
  line-height: 1.6;
  color: #C2B6A9;
}

.suite-feature-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
}

.suite-feature-item {
  display: flex;
  gap: 14px;
}

.feature-icon-badge {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(230, 183, 74, 0.12);
  border: 1px solid rgba(230, 183, 74, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-body h3 {
  font-size: 14.5px;
  font-weight: 600;
  color: #FAF8F5;
  margin-bottom: 3px;
}

.feature-body p {
  font-size: 12.5px;
  color: #B5A89A;
  line-height: 1.45;
  margin: 0;
}

.suite-testimonial-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(230, 183, 74, 0.16);
  border-radius: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
}

.testi-stars {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-bottom: 8px;
}

.testi-badge {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #E6B74A;
  background: rgba(230, 183, 74, 0.14);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
}

.testi-text {
  font-size: 12px;
  line-height: 1.55;
  color: #D8CCC0;
  font-style: italic;
  margin-bottom: 12px;
}

.testi-profile {
  display: flex;
  align-items: center;
  gap: 10px;
}

.testi-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #9E6D2B;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}

.testi-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #FFFFFF;
}

.testi-role {
  font-size: 11px;
  color: #9C8E80;
}

.suite-support-box {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #BDB0A3;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.suite-support-box strong {
  color: #E6B74A;
}

/* RIGHT PANEL (Clean Luxury White / Warm Ivory Form) */
.suite-form-panel {
  flex: 1;
  background: #FFFDFB;
  padding: 44px 48px;
  display: flex;
  flex-direction: column;
}

.form-panel-top {
  margin-bottom: 24px;
}

.gold-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9E6D2B;
}

.form-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 30px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 6px;
}

.form-subtitle {
  font-size: 13.5px;
  color: #6C5F53;
  margin: 0;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.luxury-field {
  margin-bottom: 8px;
}

.field-label {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: #3C3229;
  margin-bottom: 5px;
}

.req {
  color: #C53030;
  margin-left: 3px;
}

/* Vuetify overrides for luxury crisp inputs */
.luxury-input-field >>> .v-input__slot {
  background-color: #FAF7F2 !important;
  border-color: #E6DCCF !important;
  border-radius: 10px !important;
  min-height: 44px !important;
  transition: all 0.2s ease;
}

.luxury-input-field >>> .v-input__slot:hover {
  border-color: #9E6D2B !important;
}

.luxury-input-field.v-input--is-focused >>> .v-input__slot {
  border-color: #9E6D2B !important;
  box-shadow: 0 0 0 3px rgba(158, 109, 43, 0.14) !important;
}

.luxury-input-field >>> input,
.luxury-input-field >>> .v-select__selection,
.luxury-input-field >>> textarea {
  font-size: 13.5px !important;
  color: #1E1815 !important;
}

/* Quick Slot Pills */
.quick-pills-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  background: #F9F5EF;
  border-radius: 12px;
  border: 1px dashed rgba(158, 109, 43, 0.25);
}

.pills-label {
  font-size: 12px;
  font-weight: 600;
  color: #7D6B5A;
}

.pills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.time-pill-btn {
  background: #FFFFFF;
  border: 1px solid #DED3C4;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #5A4B3E;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}

.time-pill-btn:hover {
  border-color: #9E6D2B;
  color: #9E6D2B;
}

.time-pill-btn.active {
  background: #9E6D2B;
  border-color: #9E6D2B;
  color: #FFFFFF;
  box-shadow: 0 2px 8px rgba(158, 109, 43, 0.3);
}

/* Tag Pills */
.tags-flex-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-pill-btn {
  background: #F9F5EF;
  border: 1px solid #E4D9CA;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #55483B;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.tag-pill-btn:hover {
  border-color: #9E6D2B;
  color: #9E6D2B;
  background: #F4EDE0;
}

.tag-pill-btn.selected {
  background: #F3E9D9;
  border-color: #9E6D2B;
  color: #7D5117;
  font-weight: 600;
}

.tag-symbol {
  font-weight: 700;
}

/* Submit Action Container */
.form-submit-container {
  margin-top: 16px;
}

.submit-gold-btn {
  width: 100%;
  padding: 15px 24px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #A4712E 0%, #8A591B 100%);
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(158, 109, 43, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
}

.submit-gold-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(158, 109, 43, 0.4);
}

.submit-gold-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-arrow {
  transition: transform 0.2s ease;
}

.submit-gold-btn:hover .btn-arrow {
  transform: translateX(4px);
}

.spinner-loader {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-guarantee-badges {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  font-size: 11.5px;
  color: #73675C;
}

.guarantee-badge {
  display: inline-flex;
  align-items: center;
}

/* ==========================================================================
   ROADMAP SECTION
   ========================================================================== */
.demo-roadmap-section {
  padding: 64px 0;
  background: #F4EFEA;
  border-top: 1px solid rgba(158, 109, 43, 0.12);
  border-bottom: 1px solid rgba(158, 109, 43, 0.12);
}

.section-title-wrap {
  margin-bottom: 44px;
}

.section-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 34px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 15px;
  color: #6C5F53;
  max-width: 620px;
  margin: 0 auto;
}

.roadmap-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.roadmap-card {
  background: #FFFFFF;
  border-radius: 20px;
  padding: 36px 28px;
  position: relative;
  border: 1px solid rgba(158, 109, 43, 0.14);
  box-shadow: 0 8px 24px rgba(44, 34, 30, 0.04);
  transition: transform 0.3s ease;
}

.roadmap-card:hover {
  transform: translateY(-4px);
}

.roadmap-card.highlighted-step {
  background: linear-gradient(145deg, #201814 0%, #15100D 100%);
  border-color: rgba(230, 183, 74, 0.35);
  box-shadow: 0 12px 30px rgba(24, 19, 16, 0.25);
}

.roadmap-number {
  font-family: 'Cinzel', serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #9E6D2B;
  margin-bottom: 16px;
}

.highlighted-step .roadmap-number {
  color: #E6B74A;
}

.roadmap-icon-wrap {
  margin-bottom: 18px;
}

.roadmap-card-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 10px;
}

.roadmap-card-desc {
  font-size: 13.5px;
  line-height: 1.6;
  color: #615549;
  margin: 0;
}

.text-white {
  color: #FFFFFF !important;
}

.text-white-80 {
  color: #D6CBC0 !important;
}

/* ==========================================================================
   FAQ SECTION
   ========================================================================== */
.demo-faq-section {
  padding: 72px 0;
}

.faq-accordion-container {
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.faq-box {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid rgba(158, 109, 43, 0.16);
  padding: 18px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.faq-box:hover {
  border-color: #9E6D2B;
}

.faq-box.open {
  border-color: #9E6D2B;
  box-shadow: 0 6px 20px rgba(158, 109, 43, 0.08);
}

.faq-question-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.faq-q-text {
  font-size: 15.5px;
  font-weight: 600;
  color: #241D17;
}

.faq-toggle-icon {
  color: #9E6D2B !important;
}

.faq-answer-body {
  padding-top: 14px;
  border-top: 1px solid #F0E8DD;
  margin-top: 12px;
}

.faq-answer-body p {
  font-size: 14px;
  line-height: 1.65;
  color: #63574B;
  margin: 0;
}

/* ==========================================================================
   FOOTER
   ========================================================================== */
.demo-footer {
  background: #14100E;
  color: #FAF8F5;
  padding: 48px 0 28px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  gap: 32px;
  flex-wrap: wrap;
}

.footer-brand-col {
  max-width: 480px;
}

.footer-motto {
  font-size: 13px;
  line-height: 1.6;
  color: #A39688;
  margin-top: 10px;
}

.footer-contact-col {
  text-align: right;
}

.footer-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #E6B74A;
  margin-bottom: 6px;
}

.footer-phone {
  font-size: 18px;
  font-weight: 700;
  color: #FFFFFF;
}

.footer-email {
  font-size: 13px;
  color: #A39688;
}

.footer-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #8A7D70;
}

.footer-badges {
  display: flex;
  gap: 16px;
}

.cert-pill {
  color: #D4AF37;
  font-weight: 600;
}

/* ==========================================================================
   RESPONSIVE DESIGN
   ========================================================================== */
@media (max-width: 1024px) {
  .luxury-split-card {
    flex-direction: column;
  }
  .suite-info-panel {
    flex: 0 0 auto;
    padding: 36px 28px;
  }
  .suite-form-panel {
    padding: 36px 28px;
  }
  .roadmap-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .demo-hero-title {
    font-size: 34px;
  }
  .form-row-2 {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .footer-contact-col {
    text-align: left;
  }
  .footer-bottom-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
